import * as React from 'react';
import {
  Alert, Box, Button, Chip, FormControl, Grid, Paper, TextField, Typography,
} from '@mui/material';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { grey, orange } from '@mui/material/colors';
import moment from 'moment';
import 'moment/locale/pt-br';

const QUICK_SUGGESTIONS = [
  "Equipe ciente, monitorando",
  "Verificado, sem impacto",
  "Em tratamento",
  "Contato com cliente realizado",
  "Escalonado para N2",
];

const SEVERITY_LABELS = {
  1: 'Info', 2: 'Aviso', 3: 'Media', 4: 'Alta', 5: 'Desastre', 6: 'API Down', 7: 'Backup',
};

const SEVERITY_COLORS = {
  1: '#9e9e9e', 2: '#42a5f5', 3: '#fbc02d', 4: '#fb8c00', 5: '#e53935', 6: '#c62828', 7: '#ef5350',
};

const METHOD_COLORS = {
  telefone: 'primary', whatsapp: 'success', email: 'warning', teams: 'info', outro: 'default',
};

export default function TabReconhecimento({ props, state, handleChange, acks, escalations = [], onSubmit }) {
  const handleSuggestionClick = (text) => {
    handleChange({
      target: {
        name: 'description',
        value: state.description ? state.description + '\n' + text : text,
      },
    });
  };

  return (
    <Grid container spacing={2}>
      {/* Contexto */}
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>Contexto</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`Sev: ${SEVERITY_LABELS[props.severity] || props.severity}`}
              size="small"
              sx={{ backgroundColor: SEVERITY_COLORS[props.severity], color: '#fff', fontWeight: 600 }}
            />
            <Chip label={`Source: ${props.source || 'zabbix'}`} size="small" variant="outlined" />
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <b>Host:</b> {props.hostname || '—'} &nbsp; <b>IP:</b> {props.ip || '—'}
          </Typography>
          <Typography variant="body2">
            <b>Horario:</b> {props.clock ? moment(props.clock).locale('pt-br').format('DD/MM/YYYY HH:mm:ss') : '—'}
            {props.clock && ` (${moment(props.clock).locale('pt-br').fromNow()})`}
          </Typography>
          {props.occurrenceCount > 1 && (
            <Typography variant="body2">
              <b>Ocorrencias:</b> {props.occurrenceCount}
            </Typography>
          )}
        </Paper>

        {/* Tags */}
        {props.tags && props.tags.trim() !== "" && (
          <Alert sx={{ mb: 1 }} severity="warning">
            <Typography>{props.tags}</Typography>
          </Alert>
        )}

        {/* Acionamentos realizados */}
        {escalations.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: orange[300] }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <ContactPhoneIcon fontSize="small" color="warning" />
              <Typography variant="subtitle2">
                Acionamentos ({escalations.length})
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
              {escalations.map((esc, i) => (
                <Box key={esc.id || i} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {moment(esc.created_at).locale('pt-br').format('DD/MM HH:mm')} — {esc.user_name || ''}
                  </Typography>
                  <Typography variant="body2">
                    <b>{esc.contato_nome}</b>{' '}
                    <Chip label={esc.method} size="small" color={METHOD_COLORS[esc.method] || 'default'} sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Typography>
                  {esc.observation && (
                    <Typography variant="caption" color="text.secondary">{esc.observation}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </Grid>

      {/* ACK */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <TextField
            inputProps={{ maxLength: 255 }}
            multiline rows={6} required autoFocus
            margin="dense" id="description" name="description"
            label="Descricao:" type="text" fullWidth
            value={state.description} onChange={handleChange}
            variant="outlined" size="small"
          />
        </FormControl>

        <Box sx={{ mt: 1, mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {QUICK_SUGGESTIONS.map((s, i) => (
            <Chip key={i} label={s} size="small" variant="outlined"
              onClick={() => handleSuggestionClick(s)}
              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            />
          ))}
        </Box>

        {/* Acks anteriores */}
        {acks.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Acks anteriores</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {acks.map((ack, i) => (
                <Alert key={i} sx={{ mb: 0.5, backgroundColor: grey[1000], py: 0 }} severity="info" icon={false}>
                  <Typography variant="caption" color="text.secondary">
                    {moment(ack.clock).locale('pt-br').format('DD/MM/YYYY HH:mm')} - {moment(ack.clock).locale('pt-br').fromNow()}
                  </Typography>
                  <Typography variant="body2">{ack.description}</Typography>
                </Alert>
              ))}
            </Box>
          </Paper>
        )}
      </Grid>

      {onSubmit && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" disabled={!state.description} onClick={onSubmit}>
              Reconhecer
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
}
